// ===================================================
// src/components/Planning/UploadSalesTargetsModal.js
// ===================================================
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { X, Save } from "lucide-react";

const UploadSalesTargetsModal = ({ onClose }) => {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [percentages, setPercentages] = useState(null);

  // 🧮 Load percentages from DB
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/plan/percentages");
        const data = await res.json();
        if (data.success) {
          setPercentages({
            sales: data.sales || {},
            ml: data.ml_sales || {},
          });
          console.log("✅ Loaded percentages from DB");
        }
      } catch (err) {
        console.error("❌ Failed to load percentages:", err);
      }
    })();
  }, []);

  // 📥 Handle Excel upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const parsed = [];
      for (let i = 1; i < data.length; i++) {
        const row = data[i];
        if (!row[0] || !row[1]) continue;

        let dateValue = row[0];
        let date;
        if (typeof dateValue === "number") {
          const excelEpoch = new Date(Date.UTC(1899, 11, 30));
          date = new Date(excelEpoch.getTime() + dateValue * 86400000);
        } else {
          date = new Date(dateValue);
        }

        const formattedDate = date.toISOString().split("T")[0];
        const totalSales = parseFloat(row[1]) || 0;
        parsed.push({ date: formattedDate, totalSales });
      }

      setRows(parsed);
    };
    reader.readAsBinaryString(file);
  };

  // 🚀 Upload
  const handleUpload = async () => {
    if (!rows.length) return alert("Please upload a valid Excel file first.");
    if (!percentages) return alert("Percentages not loaded yet — please wait.");

    if (!window.confirm(`Upload ${rows.length} daily targets to forecast plan?`)) return;
    setUploading(true);

    try {
      const { sales, ml } = percentages;

      for (const row of rows) {
        const { date, totalSales } = row;
        const fSales = {};

        // 1️⃣ Calculate raw float values
      Object.keys(sales).forEach((key) => {
        const groupPercent = sales[key] || 0;
        const groupValue = (totalSales * groupPercent) / 100;
        const split = ml[key] || { m: 50, l: 50 };
        const totalSplit = (split.m || 50) + (split.l || 50);

        fSales[`${key}_m`] = Math.round((groupValue * split.m) / totalSplit);
        fSales[`${key}_l`] = Math.round((groupValue * split.l) / totalSplit);
      });


        // 2️⃣ Fix rounding so total = exactly totalSales
        const keys = Object.keys(fSales);
        const rounded = {};
        let computedTotal = 0;

        // First round normally
        keys.forEach((k) => {
          rounded[k] = Math.floor(fSales[k]);
          computedTotal += rounded[k];
        });


        // Compute difference
        const diff = Math.round(totalSales - computedTotal);
        if (diff !== 0 && keys.length > 0) {
          rounded[keys[keys.length - 1]] += diff;
        }


        const checkTotal = Object.values(rounded).reduce((a, b) => a + b, 0);
        console.log(`📅 ${date}: target=${totalSales}, computed=${checkTotal}`);

        // 3️⃣ Build final forecast
        const fProd = {
          us_cityplay_m: 0,
          us_cityplay_l: 0,
          us_20_m: 0,
          us_20_l: 0,
          eu_cityplay_m: 0,
          eu_cityplay_l: 0,
          eu_20_m: 0,
          eu_20_l: 0,
        };

        const fShip = {
          us_days: 20,
          eu_days: 15,
          uk_customer: 5,
          us_customer: 7,
        };

        const payload = {
          days: [
            {
              date,
              forecast: {
                sales: rounded,
                production: fProd,
                shipping: fShip,
              },
            },
          ],
        };

        const res = await fetch("/api/plan/forecast", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) console.error(`❌ Failed for ${date}: ${res.statusText}`);
        else console.log(`✅ Uploaded ${date} (${totalSales})`);
      }

      alert("✅ All sales targets uploaded successfully!");
      onClose();
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed — see console for details.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h3 style={{ fontWeight: 600, fontSize: "1.25rem" }}>
            📤 Upload Daily Sales Targets
          </h3>
          <button onClick={onClose} style={closeBtn}>
            <X size={20} />
          </button>
        </div>

        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ marginBottom: "1rem" }}
          />
          <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
            Expected columns: <strong>Date</strong> |{" "}
            <strong>Total Sales Target</strong>
          </div>
        </div>

        {rows.length > 0 && (
          <div style={{ maxHeight: "250px", overflowY: "auto", marginBottom: "1rem" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Sales Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td>{r.totalSales}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "right" }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <Save size={16} style={{ marginRight: "0.4rem" }} />
            {uploading ? "Uploading…" : "Upload to Server"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Styles =====
const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};
const modal = {
  background: "white",
  padding: "1.5rem 2rem",
  borderRadius: "10px",
  width: "600px",
  boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  overflowY: "auto",
  maxHeight: "90vh",
};
const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "0.5rem",
};
const closeBtn = { background: "none", border: "none", cursor: "pointer" };
const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
table.th = table.td = {
  border: "1px solid #e5e7eb",
  padding: "0.4rem 0.6rem",
  textAlign: "left",
};

export default UploadSalesTargetsModal;
